<div class="page">
    <div class="page-header">
        <div class="page-title-row">
            <h1 class="page-title">Part Coding Admin</h1>
            <button
                type="button"
                class="help-button"
                aria-label="How to use Admin Part Coding"
                (click)="helpPopover.toggle($event)">
                <i class="pi pi-question-circle"></i>
            </button>
            <p-popover
                #helpPopover
                styleClass="part-coding-help"
                appendTo="body">
                <div class="help-board">
                    <div>
                        <p class="help-kicker">What to do</p>
                        <ol class="admin-steps">
                            <li>
                                <strong>Read the mapping.</strong>
                                Customer Description is what the customer asked for. Part Code and RK Description are
                                the RK part that would be saved with it.
                            </li>
                            <li>
                                <strong>Decide every line.</strong>
                                Approve writes that pair into the customer's match history. Disapprove does not. Use
                                Approve or Reject on the row.
                            </li>
                        </ol>
                    </div>
                    <div>
                        <p class="help-kicker">Ways to do it</p>
                        <ul class="admin-tips">
                            <li>Tick the boxes, then use <strong>Approve selected</strong> or <strong>Reject selected</strong>.</li>
                            <li>
                                Or click <strong>Export</strong>, open the file, and in the <strong>Approval</strong>
                                column type only Approve or Disapprove. Leave the cell blank to skip a line. Upload
                                the file back. Do not change the other columns.
                            </li>
                            <li>
                                The info button has the rest of the match: status, source, confidence, inventory,
                                market basket, and the batch line.
                            </li>
                        </ul>
                    </div>
                </div>
            </p-popover>
            @if (showingLabel()) {
                <span class="page-subcount">{{ showingLabel() }}</span>
            }
        </div>

        <div class="user-chip">
            <span class="avatar">{{ initials(signedInUser()) }}</span>
            <span class="signed-in-name">{{ signedInUser() }}</span>
        </div>
    </div>

    <div class="table-card p-card">
        <div class="toolbar">
            <p-select
                [options]="customerOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="customerFilter()"
                (ngModelChange)="setCustomerFilter($event)"
                [showClear]="true"
                placeholder="Customer: All"
                ariaLabel="Filter by customer" />
            <p-select
                [options]="isrOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="isrFilter()"
                (ngModelChange)="setIsrFilter($event)"
                [showClear]="true"
                placeholder="ISR: All"
                ariaLabel="Filter by ISR" />
            <p-select
                [options]="dateOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="dateFilter()"
                (ngModelChange)="setDateFilter($event)"
                [showClear]="true"
                placeholder="Date: All"
                ariaLabel="Filter by date" />
            <p-select
                [options]="batchOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="batchFilter()"
                (ngModelChange)="setBatchFilter($event)"
                [showClear]="true"
                placeholder="Batch: All"
                ariaLabel="Filter by batch" />
            <span class="spacer"></span>
            <p-button
                label="Upload"
                [outlined]="true"
                (onClick)="startUpload(uploadInput)" />
            <p-splitbutton
                label="Export"
                [outlined]="true"
                [model]="exportItems"
                (onClick)="exportQueue('all')" />
            <input
                #uploadInput
                type="file"
                accept=".csv,text/csv,text/plain"
                hidden
                (change)="onUploadSelected($event)" />
        </div>

        <div class="summary">
            <span class="total">Total: {{ baseLines().length }}</span>
            @for (status of statuses; track status) {
                <button
                    type="button"
                    class="status-pill"
                    [class]="'status-pill-' + statusSeverity(status)"
                    [attr.aria-pressed]="statusFilter().has(status)"
                    (click)="toggleStatusFilter(status)">
                    {{ status }} {{ statusCounts()[status] }}
                </button>
            }
            <input
                pInputText
                type="search"
                class="search-input"
                placeholder="Search lines…"
                aria-label="Search lines"
                [ngModel]="searchQuery()"
                (ngModelChange)="setSearchQuery($event)" />
            <span class="spacer"></span>
            @if (selectedCount()) {
                <span class="bulk">
                    <span class="bulk-count">{{ selectedCount() }} selected</span>
                    <p-button
                        label="Approve selected"
                        (onClick)="decideSelected('approved')" />
                    <p-button
                        label="Reject selected"
                        severity="danger"
                        [outlined]="true"
                        (onClick)="decideSelected('rejected')" />
                    <p-button
                        label="Clear"
                        [text]="true"
                        (onClick)="clearSelection()" />
                </span>
            }
        </div>

        <p-table
            [value]="visibleLines()"
            dataKey="id"
            class="p-datatable-sm part-coding-table"
            [scrollable]="true"
            scrollHeight="flex"
            sortMode="single">
            <ng-template pTemplate="header">
                <tr>
                    <th class="col-check">
                        <input
                            type="checkbox"
                            aria-label="Select all pending lines in view"
                            [checked]="allSelected()"
                            [indeterminate]="someSelected()"
                            [disabled]="!selectableLines().length"
                            (change)="toggleAllSelected($any($event.target).checked)" />
                    </th>
                    <th
                        pSortableColumn="customer"
                        class="col-customer">
                        Customer
                        <p-sortIcon field="customer" />
                    </th>
                    <th
                        pSortableColumn="customerDescription"
                        class="col-desc">
                        Customer Description
                        <p-sortIcon field="customerDescription" />
                    </th>
                    <th
                        pSortableColumn="partCode"
                        class="col-part">
                        Part Code
                        <p-sortIcon field="partCode" />
                    </th>
                    <th
                        pSortableColumn="rkDescription"
                        class="col-desc">
                        RK Description
                        <p-sortIcon field="rkDescription" />
                    </th>
                    <th class="col-review">Details</th>
                    <th class="col-approval">Approval</th>
                </tr>
            </ng-template>

            <ng-template
                pTemplate="body"
                let-row>
                <tr
                    [class.decided]="row.approval"
                    [class.selected-row]="selectedIds().has(row.id)">
                    <td class="col-check">
                        @if (!row.approval) {
                            <input
                                type="checkbox"
                                [attr.aria-label]="'Select ' + row.customer + ' line ' + row.mark"
                                [checked]="selectedIds().has(row.id)"
                                (change)="toggleSelected(row.id, $any($event.target).checked)" />
                        }
                    </td>
                    <td class="col-customer">{{ row.customer }}</td>
                    <td class="col-desc">{{ row.customerDescription }}</td>
                    <td class="col-part">
                        @if (row.status === "RFQ") {
                            <span class="code">RFQ</span>
                        } @else {
                            <span class="code">{{ row.partCode }}</span>
                        }
                    </td>
                    <td class="col-desc">
                        @if (row.status === "RFQ") {
                            <span class="placeholder">Insert RFQ description</span>
                        } @else {
                            {{ row.rkDescription }}
                        }
                    </td>
                    <td class="col-review">
                        <p-button
                            icon="pi pi-info-circle"
                            [text]="true"
                            [rounded]="true"
                            ariaLabel="Show details"
                            (onClick)="openReasonPopover($event, row)" />
                    </td>
                    <td class="col-approval">
                        <p-tag
                            [value]="approvalLabel(row.approval)"
                            [severity]="approvalSeverity(row.approval)" />
                        @if (row.approval && row.decidedAt !== null) {
                            <div class="audit">{{ row.decidedBy }} · {{ formatSubmitted(row.decidedAt) }}</div>
                        } @else {
                            <p-button
                                icon="pi pi-check-circle"
                                [text]="true"
                                [rounded]="true"
                                severity="success"
                                ariaLabel="Approve line"
                                (onClick)="decideLine(row, 'approved')" />
                            <p-button
                                icon="pi pi-times-circle"
                                [text]="true"
                                [rounded]="true"
                                severity="danger"
                                ariaLabel="Reject line"
                                (onClick)="decideLine(row, 'rejected')" />
                        }
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td
                        [attr.colspan]="7"
                        class="empty-row">
                        @if (!hasQueue()) {
                            No lines in the queue yet — export coded lines from Part Coding to send them here.
                        } @else {
                            Nothing in the queue for these filters.
                            <button
                                type="button"
                                class="link-button"
                                (click)="clearFilters()">
                                Clear filters
                            </button>
                        }
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<p-popover
    #reasonPopover
    appendTo="body"
    styleClass="reason-popover"
    (onHide)="activeLine.set(null)">
    @if (activeLine(); as line) {
        <h3 class="reason-title">The mapping</h3>
        <p class="reason-text">{{ line.customerDescription }}</p>
        <p class="reason-text">
            @if (line.status === "RFQ") {
                No RK part. This would be saved as an RFQ.
            } @else {
                {{ line.partCode }} — {{ line.rkDescription }}
            }
        </p>
        <h3 class="reason-title">Match details</h3>
        <dl class="detail-list">
            <div>
                <dt>Status</dt>
                <dd>{{ line.status }}</dd>
            </div>
            <div>
                <dt>Source</dt>
                <dd>{{ line.source }}</dd>
            </div>
            <div>
                <dt>Confidence</dt>
                <dd>{{ line.confidence }}%</dd>
            </div>
            <div>
                <dt>Inventory</dt>
                <dd>{{ line.inventory === null ? "—" : line.inventory }}</dd>
            </div>
            <div>
                <dt>Market basket</dt>
                <dd>{{ line.isMarketBasket === null ? "—" : line.isMarketBasket ? "Yes" : "No" }}</dd>
            </div>
            <div>
                <dt>Batch</dt>
                <dd>{{ line.batchId ?? "—" }}</dd>
            </div>
            <div>
                <dt>Batch line</dt>
                <dd>{{ line.mark }}</dd>
            </div>
            <div>
                <dt>Submitted</dt>
                <dd>{{ line.isr }} · {{ formatSubmitted(line.submittedAt) }}</dd>
            </div>
        </dl>
        <h3 class="reason-title">Why this part</h3>
        <p class="reason-text">{{ line.reason || "No reason recorded." }}</p>
        <h3 class="reason-title">Decision</h3>
        @if (line.approval && line.decidedAt !== null) {
            <p class="reason-text">
                {{ approvalLabel(line.approval) }} by {{ line.decidedBy }} on {{ formatSubmitted(line.decidedAt) }}
            </p>
        } @else {
            <p class="reason-text">Not decided yet. Approve writes this mapping into match history. Reject does not.</p>
        }
    }
</p-popover>

<p-dialog
    header="Upload rejected"
    [modal]="true"
    [visible]="uploadDialogVisible()"
    (visibleChange)="uploadDialogVisible.set($event)"
    [style]="{ width: '520px' }">
    <p class="upload-banner">
        {{ uploadFileName() }} was not applied. Fix {{ uploadErrors().length }} row{{
            uploadErrors().length === 1 ? "" : "s"
        }}
        and upload again — nothing in the queue was changed.
    </p>
    <ul class="upload-errors">
        @for (error of uploadErrors().slice(0, 20); track error.row) {
            <li>
                <span class="upload-row">Row {{ error.row }}</span>
                — {{ error.message }}
            </li>
        }
        @if (uploadErrors().length > 20) {
            <li>…and {{ uploadErrors().length - 20 }} more.</li>
        }
    </ul>
    <ng-template pTemplate="footer">
        <p-button
            label="Fix and re-upload"
            (onClick)="uploadDialogVisible.set(false)" />
    </ng-template>
</p-dialog>
